import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const ALL_PERMS = [
  { group: 'Tenant', perms: ['tenant.profile.update','tenant.billing.view','tenant.billing.manage','tenant.teams.create','tenant.teams.manage','tenant.members.invite','tenant.members.manage','tenant.api_keys.create','tenant.api_keys.manage','tenant.analytics.view','tenant.audit_logs.view','tenant.white_label.manage','tenant.workspaces.create','tenant.workspaces.manage'] },
  { group: 'Team',   perms: ['team.manage','team.members.add','team.members.remove','team.reports.view','team.tasks.assign','team.workflow.manage'] },
  { group: 'Work',   perms: ['work.access','work.status.update','work.interactions.manage','work.reports.view.own','work.documents.upload'] },
  { group: 'API',    perms: ['api.sandbox.access','api.production.access','api.debug','api.analytics.view','api.rate_limit.view','api.support_tickets.create'] },
];

export default function RolePermissions() {
  const [roles, setRoles]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', permissions: [] });

  const load = async () => {
    setLoading(true);
    try { const r = await api.get('/tenant/roles'); setRoles(r.data); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const togglePerm = (perm) => {
    setForm(f => ({
      ...f,
      permissions: f.permissions.includes(perm)
        ? f.permissions.filter(p => p !== perm)
        : [...f.permissions, perm],
    }));
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Role name required'); return; }
    try {
      if (editing) {
        await api.put(`/tenant/roles/${editing.id}`, form);
        toast.success('Role updated');
      } else {
        await api.post('/tenant/roles', form);
        toast.success('Role created');
      }
      setEditing(null); setCreating(false);
      setForm({ name: '', description: '', permissions: [] });
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const deleteRole = async (id) => {
    if (!window.confirm('Delete this role?')) return;
    try { await api.delete(`/tenant/roles/${id}`); toast.success('Role deleted'); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const startEdit = (role) => {
    setEditing(role);
    setForm({ name: role.name, description: role.description || '', permissions: role.permissions || [] });
    setCreating(true);
  };

  const S = { card: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, marginBottom: 12 } };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600 }}>Custom Roles</h2>
        <button onClick={() => { setEditing(null); setForm({ name: '', description: '', permissions: [] }); setCreating(!creating); }} className="btn btn-primary">＋ New Role</button>
      </div>

      {creating && (
        <div style={{ ...S.card, marginBottom: 20 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>{editing ? 'Edit Role' : 'Create Role'}</h3>
          <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Role name *" className="input" style={{ width: '100%', marginBottom: 8 }} />
          <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Description" className="input" style={{ width: '100%', marginBottom: 16 }} />
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Permissions ({form.permissions.length} selected)</div>
          {ALL_PERMS.map(group => (
            <div key={group.group} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 600, letterSpacing: 1, marginBottom: 6 }}>{group.group.toUpperCase()}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {group.perms.map(perm => (
                  <button key={perm} onClick={() => togglePerm(perm)}
                    style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, border: '1px solid var(--border)', background: form.permissions.includes(perm) ? 'var(--accent)' : 'transparent', color: form.permissions.includes(perm) ? '#fff' : 'var(--text-2)', cursor: 'pointer' }}>
                    {perm.split('.').slice(1).join('.')}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button onClick={save} className="btn btn-primary">Save role</button>
            <button onClick={() => { setCreating(false); setEditing(null); }} className="btn btn-ghost">Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>Loading…</div> : (
        roles.length === 0
          ? <div style={{ ...S.card, textAlign: 'center', color: 'var(--text-3)', padding: 40 }}>No custom roles yet.</div>
          : roles.map(r => (
            <div key={r.id} style={S.card}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <strong style={{ fontSize: 14 }}>{r.name}</strong>
                    {r.is_system && <span style={{ fontSize: 10, background: '#fab38722', color: '#fab387', padding: '1px 6px', borderRadius: 4 }}>SYSTEM</span>}
                  </div>
                  {r.description && <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 6 }}>{r.description}</div>}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {(r.permissions || []).slice(0, 6).map(p => (
                      <code key={p} style={{ fontSize: 10, background: 'var(--bg)', padding: '1px 6px', borderRadius: 4, color: 'var(--text-2)' }}>{p}</code>
                    ))}
                    {(r.permissions || []).length > 6 && <code style={{ fontSize: 10, background: 'var(--bg)', padding: '1px 6px', borderRadius: 4, color: 'var(--text-3)' }}>+{r.permissions.length - 6} more</code>}
                  </div>
                </div>
                {!r.is_system && (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => startEdit(r)} className="btn btn-ghost" style={{ fontSize: 12 }}>Edit</button>
                    <button onClick={() => deleteRole(r.id)} className="btn btn-ghost" style={{ fontSize: 12, color: '#f38ba8' }}>Delete</button>
                  </div>
                )}
              </div>
            </div>
          ))
      )}
    </div>
  );
}
